<template>
  <Transition name="drawer">
    <div
      v-if="isOpen"
      class="fixed inset-0 drawer-wrapper"
    >
      <!-- Backdrop/overlay - full width on mobile, partial on desktop -->
      <div
        class="fixed inset-y-0 right-0 bg-kb-bg drawer-overlay"
        style="width: 100vw; min-width: 600px; max-width: 700px;"
        @click="$emit('close')"
      />

      <!-- Drawer container - purposely NOT using tech-panel class -->
      <div class="fixed top-[52px] bottom-0 right-0 sci-fi-drawer">
        <!-- Header -->
        <div class="px-4 py-3 border-b border-kb-line flex justify-between items-center gap-3 sci-fi-drawer-header">
          <div class="flex items-center min-w-0">
            <Button
              v-if="canGoBack"
              icon="pi pi-arrow-left"
              text
              rounded
              severity="secondary"
              size="small"
              class="mr-1"
              aria-label="Back"
              @click="$emit('back')"
            />
            <h3 class="m-0 text-base font-semibold text-kb-fg truncate">
              {{ title }}
            </h3>
            <!-- Copy buttons for member data -->
            <div
              v-if="entity?.id"
              class="kb-drawer-copy flex items-center ml-2 shrink-0"
            >
              <button
                v-tooltip.top="'Copy name'"
                type="button"
                aria-label="Copy name"
                @click="copyToClipboard(entity?.name || title, 'Name')"
              >
                <i class="pi pi-user" />
              </button>
              <button
                v-if="entity?.email"
                v-tooltip.top="'Copy email'"
                type="button"
                aria-label="Copy email"
                @click="copyToClipboard(entity?.email ?? '', 'Email')"
              >
                <i class="pi pi-envelope" />
              </button>
              <button
                v-tooltip.top="'Copy UID'"
                type="button"
                aria-label="Copy UID"
                @click="copyToClipboard(entity?.id ?? '', 'UID')"
              >
                <i class="pi pi-copy" />
              </button>
            </div>
          </div>
          <Button
            icon="pi pi-times"
            text
            rounded
            severity="secondary"
            aria-label="Close"
            @click="$emit('close')"
          />
        </div>

        <!-- Content area -->
        <div class="drawer-content kb-autoscrollbar p-4">
          <!-- Keyed on the entity, so another member or SubKrewe gets a fresh
               instance instead of the previous one's state -->
          <component
            :is="content?.component"
            v-if="content?.component"
            :key="entity?.id"
            :data="content?.data"
            v-bind="contentProps || {}"
            @close="$emit('close')"
            @reset-history="$emit('reset-history')"
            @update:title="updateDrawerTitle"
          />
          <div
            v-else
            class="p-4 text-center text-[13px] text-kb-muted"
          >
            Select an item to view details
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { onMounted, onUnmounted } from 'vue'
import type { PropType } from 'vue'
import type { DrawerContent } from '~/composables/useDrawer'

// `DrawerContent.data` is deliberately `unknown` — callers pass members,
// SubKrewes, Stripe lookups. The header only needs these three fields.
const entity = computed(() =>
  props.content?.data as { id?: string, name?: string, email?: string } | undefined,
)

const toast = useToast()
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  content: {
    type: Object as PropType<DrawerContent | null>,
    default: null,
  },
  contentProps: {
    type: Object,
    default: () => ({}),
  },
  canGoBack: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'back', 'reset-history'])

// When a child component updates the title, pass it to useDrawer
const updateDrawerTitle = (newTitle: string) => {
  const { updateDrawerTitle } = useDrawer()
  if (updateDrawerTitle) {
    updateDrawerTitle(newTitle)
  }
}

// Anything that can sit on top of the drawer and closes on Escape itself.
// These are only in the DOM while open (inline date pickers excepted), and a
// closing one is still there while this keydown runs, since Vue removes it on
// the next tick.
const OVERLAY_SELECTOR = [
  '.p-dialog-mask',
  '[role="dialog"][aria-modal="true"]',
  '[role="alertdialog"]',
  '.p-select-overlay',
  '.p-multiselect-overlay',
  '.p-autocomplete-overlay',
  '.p-datepicker-panel:not(.p-datepicker-panel-inline)',
  '.p-popover',
  '.p-menu-overlay',
  '.p-tieredmenu-overlay',
].join(', ')

// Escape closes the drawer, unless it's closing something on top of it first
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !props.isOpen || event.defaultPrevented) return
  if (document.querySelector(OVERLAY_SELECTOR)) return
  emit('close')
}

// Add and remove event listener for escape key
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

// Copy ID to clipboard with feedback
const copyToClipboard = (value: string, label: string) => {
  navigator.clipboard.writeText(value)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Copied to clipboard',
        detail: `${label} copied to clipboard`,
        life: 2000,
      })
    })
    .catch(err => console.error('Could not copy to clipboard:', err))
}
</script>

<style scoped>
/* Create a specific z-index stacking context for the drawer */
.drawer-wrapper {
  z-index: 10; /* Extremely high z-index */
  pointer-events: none; /* Allow clicks to pass through by default */
}

/* Make only the overlay and drawer clickable */
.drawer-overlay,
.sci-fi-drawer {
  pointer-events: auto;
}

.drawer-overlay {
  /* Remove the glow border from the overlay */
  box-shadow: none;
}

/* Drawer styling without relying on tech-panel - make fully opaque */
.sci-fi-drawer {
  width: 100%; /* Full width on mobile */
  max-width: 100vw; /* Full viewport width on mobile */
  background-color: var(--kb-pane);
  border-left: 1px solid var(--kb-line-2);
  z-index: 110; /* Higher than overlay */
  display: flex;
  flex-direction: column;
  transform: translateX(0);
  transition: transform 0.3s ease;
  /* Add this to ensure opacity */
  backdrop-filter: none;
  box-shadow: -24px 0 60px var(--kb-shadow);
}

/* Desktop styling for the drawer - limit width on larger screens */
@media (min-width: 768px) {
  .sci-fi-drawer {
    width: 700px;
    max-width: 90vw;
  }
}

/* Header with sci-fi styling but NO clip-path - ensure it's opaque */
.sci-fi-drawer-header {
  background: var(--kb-pane);
  position: relative;
}

/* Copy buttons: small and dim until pointed at */
.kb-drawer-copy button {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--kb-dim);
  cursor: pointer;
}

.kb-drawer-copy button:hover {
  color: var(--kb-fg);
  background: var(--kb-hover);
}

.kb-drawer-copy .pi {
  font-size: 12px;
}

/* Content area with scroll */
.drawer-content {
  flex: 1;
  overflow-y: auto;
  height: calc(100% - 52px);
}

/* Drawer animation */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

/* Animate the entire drawer as one unit instead of having the overlay fade in separately */
.drawer-enter-active .drawer-overlay,
.drawer-leave-active .drawer-overlay {
  transition: transform 0.3s ease;
}

.drawer-enter-from .drawer-overlay,
.drawer-leave-to .drawer-overlay {
  transform: translateX(100%);
}

.drawer-enter-active .sci-fi-drawer,
.drawer-leave-active .sci-fi-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from .sci-fi-drawer,
.drawer-leave-to .sci-fi-drawer {
  transform: translateX(100%);
}
</style>
